import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { CookieService } from "ngx-cookie-service";
import { map, Observable } from "rxjs";
import { environment } from "../../environments/environment";
import { AppType } from "../enums/app-type.enum";
import { PolicyType } from "../enums/policy-type.enum";
import { GetAgreements } from "../interfaces/agreements.interface";
import { LogRequest } from "../interfaces/analytics.interface";
import { GetApkQuestions } from "../interfaces/apk-questions.interface";
import {
  GlobtroterConfigResult,
  GlobtroterMenuResult,
  GlobtroterUserResult,
} from "../interfaces/globtroter.interface";
import {
  PostPurchaseBody,
  PostPurchaseResult,
} from "../interfaces/purchase.interface";
import {
  PostQuotationBody,
  PostQuotationResponse,
} from "../interfaces/quotation.interface";
import { RemotePolicyData } from "../interfaces/remote-policy-data.interface";
import { Response } from "../interfaces/response.interface";
import { PolicyStore } from "../store/policy.store";

@Injectable({
  providedIn: "root",
})
export class ApiService {
  private readonly http = inject(HttpClient);
  private readonly policyStore = inject(PolicyStore);

  getApkQuestions(): Observable<GetApkQuestions> {
    return this.http
      .get<Response<GetApkQuestions>>(this.createUrl("/apkquestions"))
      .pipe(map((resp) => resp.result));
  }

  getAgreements(): Observable<GetAgreements> {
    return this.http
      .get<Response<GetAgreements>>(this.createUrl("/agreements"))
      .pipe(map((resp) => resp.result));
  }

  owuDocumentUrl(): string {
    return `${environment.baseApiUrl}/ext/packages/santander/${this.policyStore.currentPolicyType}/Direct/owu`;
  }

  postCheckApkAnswers(apkIds: number[]) {
    return this.http.post<Response<void>>(this.createUrl("/checkapkanswers"), {
      apkAnswers: apkIds,
    });
  }

  postPurchase(body: PostPurchaseBody): Observable<PostPurchaseResult> {
    return this.http
      .post<Response<PostPurchaseResult>>(this.createUrl("/purchase"), body)
      .pipe(map((resp) => resp.result));
  }

  postQuotation(body: PostQuotationBody): Observable<PostQuotationResponse> {
    return this.http
      .post<Response<PostQuotationResponse>>(this.createUrl("/quotation"), body)
      .pipe(map((resp) => resp.result));
  }

  getPolicyInfo(remoteId: string): Observable<RemotePolicyData> {
    return this.http
      .get<Response<RemotePolicyData>>(this.createUrl(`/access/${remoteId}`))
      .pipe(map((resp) => resp.result));
  }

  postAnalyticsData(data: LogRequest): Observable<any> {
    return this.http.post<any>(environment.analyticsUrl, data);
  }

  getGlobtroterMenu(): Observable<GlobtroterMenuResult> {
    return this.http.get<GlobtroterMenuResult>(this.createAuthUrl("/layout/menu"));
  }

  getGlobtroterUser(): Observable<GlobtroterUserResult> {
    return this.http.get<GlobtroterUserResult>(this.createAuthUrl('/user/details'));
  }

  getGlobtroterConfig(): Observable<GlobtroterConfigResult> {
    return this.http.get<GlobtroterConfigResult>(this.createAuthUrl('/user/config'));
  }

  private createAuthUrl(endpoint: string): string {
    return `${environment.tokenApiUrl}${endpoint}`;
  }

  private createUrl(url: string) {
    const isDirect = environment.appType === AppType.Direct;
    const isRemote = this.policyStore.currentPolicyType === PolicyType.Remote;

    let channelPath: string;

    if (isRemote) {
      channelPath = `/packages/santander/${this.policyStore.currentPolicyType}`;
    } else if (isDirect) {
      channelPath = `/ext/packages/santander/${this.policyStore.currentPolicyType}/Direct`;
    } else {
      channelPath = `/packages/santander/${this.policyStore.currentPolicyType}/Agent`;
    }

    return environment.baseApiUrl + channelPath + url;
  }
}
